import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { Badge, Spinner } from '../components/UI';
import { auth } from '../services/firebase';
import {
  AppShell, TopNav, NavDrawer, BodyGrid, PageBody, SectionHeader, useDrawer,
} from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/api';
import ReactMarkdown from 'react-markdown';
import SubscriptionBanner, { ReferralBanner } from '../components/SubscriptionBanner';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3070';

const AGENT_TAGS = { ceo:'#0e0e0d', builder:'#378add', content:'#5dcaa5', marketing:'#c8563a', research:'#e8a87c', founder:'#0e0e0d' };

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

const PLACEHOLDER_TASKS = [
  { id:'p1', title:'Build the MVP landing page', description:'Generate and deploy a conversion-focused landing page to your raji.ai subdomain.', agent_type:'builder', status:'pending' },
  { id:'p2', title:'Define ideal customer profile', description:'Research and document the ICP — demographics, pain points, and acquisition channels.', agent_type:'marketing', status:'pending' },
  { id:'p3', title:'Write founding story and mission', description:'Craft the brand narrative and mission statement for use across all channels.', agent_type:'content', status:'pending' },
  { id:'p4', title:'Scout the competitive landscape', description:'Analyze 5 direct and indirect competitors. Summarize positioning and gaps.', agent_type:'research', status:'pending' },
];

function buildGridButtons(activeWorkspace, artifacts) {
  const wsId = activeWorkspace?.id || '';
  const countByAgent = (agentType) => artifacts.filter(a => a.agent_type === agentType).length;
  const latestByAgent = (agentType) => {
    const sorted = artifacts
      .filter(a => a.agent_type === agentType)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sorted[0]?.title || null;
  };
  const siteArtifact = artifacts.find(a => a.type === 'site');
  const siteUrl = activeWorkspace?.site_url || siteArtifact?.live_url || null;

  return [
    {
      id: 'strategy', label: 'Business Strategy', icon: '🧠',
      color: '#0e0e0d', accent: 'rgba(14,14,13,0.08)',
      description: activeWorkspace?.workspace_summary ? 'CEO analysis ready' : 'No analysis yet',
      badge: null,
      href: `/dashboard/artifacts?agent=ceo&workspace_id=${wsId}`,
      disabled: !activeWorkspace?.workspace_summary,
    },
    {
      id: 'website', label: 'AdviBook: Client Experience', icon: '🌐',
      color: '#378add', accent: 'rgba(55,138,221,0.08)',
      description: siteUrl ? 'Site deployed' : countByAgent('builder') ? `${countByAgent('builder')} document${countByAgent('builder') !== 1 ? 's' : ''}` : 'No site yet',
      badge: countByAgent('builder') || null,
      href:  `/dashboard/artifacts?agent=builder&workspace_id=${wsId}`,
      external: !!siteUrl,
      disabled: countByAgent('builder') === 0 && !siteUrl,
      latestTitle: latestByAgent('builder'),
    },
    {
      id: 'marketing', label: 'Marketing', icon: '📣',
      color: '#c8563a', accent: 'rgba(200,86,58,0.08)',
      description: countByAgent('marketing') ? `${countByAgent('marketing')} document${countByAgent('marketing') !== 1 ? 's' : ''}` : 'No documents yet',
      badge: countByAgent('marketing') || null,
      href: `/dashboard/artifacts?agent=marketing&workspace_id=${wsId}`,
      disabled: countByAgent('marketing') === 0,
      latestTitle: latestByAgent('marketing'),
    },
    {
      id: 'research', label: 'Research', icon: '🔍',
      color: '#e8a87c', accent: 'rgba(232,168,124,0.08)',
      description: countByAgent('research') ? `${countByAgent('research')} document${countByAgent('research') !== 1 ? 's' : ''}` : 'No documents yet',
      badge: countByAgent('research') || null,
      href: `/dashboard/artifacts?agent=research&workspace_id=${wsId}`,
      disabled: countByAgent('research') === 0,
      latestTitle: latestByAgent('research'),
    },
    {
      id: 'content', label: 'Content', icon: '📝',
      color: '#5dcaa5', accent: 'rgba(93,202,165,0.08)',
      description: countByAgent('content') ? `${countByAgent('content')} document${countByAgent('content') !== 1 ? 's' : ''}` : 'No documents yet',
      badge: countByAgent('content') || null,
      href: `/dashboard/artifacts?agent=content&workspace_id=${wsId}`,
      disabled: countByAgent('content') === 0,
      latestTitle: latestByAgent('content'),
    },
    {
      id: 'tasks', label: 'Tasks', icon: '✅',
      color: '#6366f1', accent: 'rgba(99,102,241,0.08)',
      description: 'View all tasks', badge: null,
      href: `/dashboard/tasks?workspace_id=${wsId}`, disabled: false,
    },
  ];
}

function GridButton({ btn, index }) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (btn.disabled || !btn.href) return;
    if (btn.external) window.open(btn.href, '_blank', 'noopener noreferrer');
    else navigate(btn.href);
  };

  return (
    <button
      onClick={handleClick}
      disabled={btn.disabled}
      style={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', justifyContent: 'space-between',
        padding: '20px 22px', background: 'var(--white)',
        border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
        cursor: btn.disabled ? 'default' : 'pointer',
        opacity: btn.disabled ? 0.5 : 1, transition: 'all 0.15s ease',
        textAlign: 'left', minHeight: '130px',
        animation: 'gridFadeIn 0.35s ease both',
        animationDelay: `${index * 55}ms`,
      }}
      onMouseEnter={e => {
        if (!btn.disabled) {
          e.currentTarget.style.borderColor = btn.color;
          e.currentTarget.style.boxShadow = `0 4px 16px ${btn.accent.replace('0.08', '0.18')}`;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--paper-3)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {btn.badge != null && (
        <span style={{
          position: 'absolute', top: '14px', right: '14px',
          background: btn.color, color: btn.id === 'research' ? '#333' : '#fff',
          fontSize: '11px', fontWeight: '600', padding: '2px 7px',
          borderRadius: '100px', lineHeight: '1.5',
        }}>{btn.badge}</span>
      )}
      <div style={{
        width: '40px', height: '40px', borderRadius: '10px',
        background: btn.accent, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '20px', marginBottom: '12px', flexShrink: 0,
      }}>{btn.icon}</div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--ink)', marginBottom: '3px', lineHeight: 1.3 }}>
          {btn.label}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--ink-soft)', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {btn.latestTitle
            ? btn.latestTitle.length > 42 ? btn.latestTitle.slice(0, 42) + '…' : btn.latestTitle
            : btn.description}
        </div>
      </div>
    </button>
  );
}

function WorkspaceStatusPanel({ workspace }) {
  const wsId       = workspace?.id || '';
  const feedLog    = workspace?.feed_log || '';
  const summary    = workspace?.workspace_summary || '';
  const hasFeed    = feedLog.trim().length > 0;
  const hasSummary = summary.trim().length > 0;
  const scrollStyle = { maxHeight: '140px', overflowY: 'auto', paddingRight: '4px' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}
      className="status-panel-grid">
      <div style={{ background: 'var(--white)', border: '1px solid var(--paper-3)', borderRadius: 'var(--r)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activity</span>
          <span style={{ fontSize: '16px' }}>⚡</span>
        </div>
        <div style={scrollStyle}>
          {hasFeed
            ? <p style={{ fontSize: '13px', color: 'var(--ink-soft)', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>{feedLog.trim()}</p>
            : <p style={{ fontSize: '13px', color: 'var(--ink-mute)', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>Agents ready — next run tonight. Your CEO agent will plan the sprint and delegate tasks at 2am.</p>
          }
        </div>
        <div style={{ display: 'flex', gap: '14px', marginTop: 'auto', paddingTop: '4px', borderTop: '1px solid var(--paper-2)', flexWrap: 'wrap' }}>
           <Link to={`/dashboard/ceo-runlog?workspace_id=${wsId}`} style={{ fontSize: '12px', color: 'var(--ink-soft)', fontWeight: '500', textDecoration: 'none', whiteSpace: 'nowrap' }}>
             CEO Report (Full)
           </Link>
           <Link to={`/dashboard/ceo_messages?workspace_id=${wsId}`} style={{ fontSize: '12px', color: 'var(--ink-soft)', fontWeight: '500', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Send Private message to CEO
          </Link>
        </div>
      </div>

      <div style={{ background: 'var(--white)', border: '1px solid var(--paper-3)', borderRadius: 'var(--r)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current State</span>
          <span style={{ fontSize: '16px' }}>🧠</span>
        </div>
        <div style={scrollStyle}>
          {hasSummary
            ? <div style={{ fontSize: '13px', color: 'var(--ink-soft)', lineHeight: 1.65 }} className="markdown-summary"><ReactMarkdown>{summary}</ReactMarkdown></div>
            : <p style={{ fontSize: '13px', color: 'var(--ink-mute)', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>No summary yet. Run the CEO agent to generate your business analysis.</p>
          }
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { activeWorkspace, tasks, artifacts, loading, refetch } = useWorkspaces();
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(false);
  const { user }     = useAuth();
  const drawer       = useDrawer();

  useEffect(() => {
    if (params.get('welcome') === '1') {
      setShowWelcome(true);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [params]);

  const displayTasks  = tasks.length ? tasks : PLACEHOLDER_TASKS;
  const isPlaceholder = tasks.length === 0;
  const gridButtons   = buildGridButtons(activeWorkspace, artifacts);

  return (
    <AppShell>
      <style>{`
        @keyframes gridFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 640px) {
          .status-panel-grid { grid-template-columns: 1fr !important; }
          .btn-grid           { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 400px) {
          .btn-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .hide-xs { display: none !important; }
        }
        .markdown-summary h1, .markdown-summary h2, .markdown-summary h3 { font-size: 13px; font-weight: 600; margin: 8px 0 4px; color: var(--ink); }
        .markdown-summary p  { margin: 0 0 6px; }
        .markdown-summary ul, .markdown-summary ol { padding-left: 16px; margin: 4px 0; }
        .markdown-summary li { margin-bottom: 2px; }
        .markdown-summary strong { color: var(--ink); }
      `}</style>

      <TopNav breadcrumb="Overview" drawerOpen={drawer.open} onToggleDrawer={drawer.toggle} drawerPinned={drawer.pinned} />
      <NavDrawer open={drawer.open} onClose={drawer.close} pinned={drawer.pinned} onTogglePin={drawer.togglePin} />

      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody>

          {showWelcome && (
            <div style={{ background: 'var(--ink)', borderRadius: 'var(--r)', padding: '20px 24px', marginBottom: '20px' }}>
              <h3 style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: 'var(--paper)', marginBottom: '6px' }}>Welcome to Raji 👋</h3>
              <p style={{ fontSize: '13px', color: 'rgba(245,242,235,0.6)', lineHeight: 1.5, marginBottom: '14px' }}>
                Your workspace is ready. Describe your business idea and agents start tonight.
              </p>
              <Link to="/onboarding" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--accent)', color: '#fff', padding: '9px 18px', borderRadius: 'var(--r)', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}>
                Set up your first business →
              </Link>
              <button onClick={() => setShowWelcome(false)} style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'rgba(245,242,235,0.4)', cursor: 'pointer', fontSize: '13px' }}>
                Dismiss
              </button>
            </div>
          )}


          {/* ── Subscription banner then Referral banner ── */}
          <SubscriptionBanner user={user} />
          <ReferralBanner user={user} />

          <SectionHeader title="Your Business" />
          <div className="btn-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '28px' }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ height: '130px', borderRadius: 'var(--r)', background: 'var(--paper-2)', animation: 'gridFadeIn 0.3s ease both', animationDelay: `${i * 40}ms` }} />
                ))
              : gridButtons.map((btn, i) => <GridButton key={btn.id} btn={btn} index={i} />)
            }
          </div>

          <SectionHeader
            title="Tasks"
            actionLabel="View all →"
            actionHref={`/dashboard/tasks?workspace_id=${activeWorkspace?.id || ''}`}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
            {loading ? <Spinner size={20} /> : displayTasks.slice(0, 4).map(task => (
              <TaskCard
                key={task.id}
                task={task}
                placeholder={isPlaceholder}
                onClick={() => navigate(`/dashboard/tasks/${task.id}?workspace_id=${activeWorkspace?.id}`)}
              />
            ))}
          </div>

        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}

function TaskCard({ task, placeholder, onClick }) {
  const statusColor = task.status === 'in_progress' ? 'dark' : task.status === 'done' ? 'green' : 'gray';
  const statusLabel = task.status === 'in_progress' ? 'Running' : task.status === 'done' ? 'Done' : 'Pending';
  return (
    <div onClick={onClick} style={{ background: 'var(--white)', border: '1px solid var(--paper-3)', borderRadius: 'var(--r)', padding: '14px 16px', cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s', opacity: placeholder ? 0.7 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', fontWeight: '500', lineHeight: 1.4 }}>{task.title}</span>
        <Badge color={statusColor}>{statusLabel}</Badge>
      </div>
      <p style={{ fontSize: '12px', color: 'var(--ink-soft)', lineHeight: 1.5, marginBottom: '10px' }}>
        {(task.description || '').slice(0, 120)}{task.description?.length > 120 ? '…' : ''}
      </p>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '100px', background: `${AGENT_TAGS[task.agent_type] || '#888'}20`, color: AGENT_TAGS[task.agent_type] || 'var(--ink-mute)' }}>
          {task.agent_type}
        </span>
        {placeholder && <span style={{ fontSize: '11px', color: 'var(--ink-mute)', alignSelf: 'center' }}>· scheduled for tonight</span>}
      </div>
    </div>
  );
}